import { cn } from '@/lib/utils'
import Image from 'next/image'
import type { ReactNode } from 'react'

// Captures are 1170x2532 (390pt at 3x), one per theme.
export const CAPTURE_WIDTH = 1170
export const CAPTURE_HEIGHT = 2532

export type AppScreen = 'dashboard' | 'dashboard-done' | 'parts' | 'maintenance'

export const captureSrc = (screen: AppScreen, theme: 'light' | 'dark') => `/landing/app/${screen}-${theme}.webp`

type ScreenContent =
  | {
      /** Capture name under /landing/app, without the -light/-dark suffix. */
      screen: AppScreen
      alt: string
      children?: never
    }
  | {
      /** Custom screen content, e.g. several captures to cross-fade. */
      children: ReactNode
      screen?: never
      alt?: never
    }

type PhoneFrameProps = ScreenContent & {
  className?: string
  /** Height of the visible screen area; defaults to the full capture. */
  screenClassName?: string
  /** Scrolls the capture up by this share of its width, to show a lower part of the screen. */
  cropTop?: number
  /** Blurs the screen so a callout on top of it reads first. */
  dimmed?: boolean
  sizes?: string
}

/** A plain rounded device frame around a real app capture. No notch or bezel art. */
export function PhoneFrame({
  screen,
  alt,
  children,
  className,
  screenClassName = 'aspect-[390/844]',
  cropTop = 0,
  dimmed = false,
  sizes = '330px',
}: PhoneFrameProps) {
  const style = cropTop ? { marginTop: `-${cropTop}%` } : undefined

  return (
    <div className={cn('rounded-4xl border border-border bg-card p-2', className)}>
      <div className={cn('relative overflow-hidden rounded-3xl border border-border/60 bg-background', screenClassName)}>
        {screen
          ? (['light', 'dark'] as const).map((theme) => (
              <Image
                key={theme}
                src={captureSrc(screen, theme)}
                alt={alt}
                width={CAPTURE_WIDTH}
                height={CAPTURE_HEIGHT}
                sizes={sizes}
                style={style}
                className={cn('w-full', theme === 'light' ? 'block dark:hidden' : 'hidden dark:block')}
              />
            ))
          : children}
        {dimmed && <div aria-hidden className="absolute inset-0 bg-background/35 backdrop-blur-xs" />}
      </div>
    </div>
  )
}
